import { PropTypes } from 'prop-types';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    multiPanelAtom,
    knowledgeViewSelectedAtom,
    listenOnChangeKnowledgeViewSelectedAtom,
    isUsingChatAIAtom
} from '@/state/atom/panelAtom';
import { destinationPrjIdAtom } from '@/state/atom/ticketKnowledgeAtom';
import Constant from '@/common/constant/Constant';
import {
    publicKnowledgeHitNumberTicketAtom
} from '@/state/atom/ticketAtom';
import { viewKnowledgeProjectSelectedAtom } from '@/state/atom/projectAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { Tooltip } from '@material-ui/core';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import { handleUpdateSearchCondition } from '../handler/ChatAIHandler';
import ReferenceKnowledgeIcon from '@/component/common/icons/ReferenceKnowledgeIcon';
import ReloadIcon from '@/component/common/icons/ReloadIcon';

import Utils from '@/common/utils/utils';
import trI18n from '@/common/utils/trI18n';
import tr from '@/common/utils/tr';
import useTranslationSubscription from '@/hooks/useTranslation';

ReferenceKnowledge.propTypes = {
    chatThreadId: PropTypes.any,
    projectInfo: PropTypes.any,
    panelId: PropTypes.any,
    tabId: PropTypes.any,
    reOpenKnowledgePanel: PropTypes.any,
    projectIdKnowledgeSelected: PropTypes.any,
    isInvalidPrecondition: PropTypes.any,
    isUsingKnowledgeService: PropTypes.any,
    toggleUsingKnowledgeTicketList: PropTypes.any,
};

export default function ReferenceKnowledge({
    chatThreadId,
    projectInfo,
    panelId,
    tabId,
    reOpenKnowledgePanel,
    projectIdKnowledgeSelected,
    isInvalidPrecondition,
    isUsingKnowledgeService,
    toggleUsingKnowledgeTicketList,
}) {
    useTranslationSubscription();
    
    const [panelList, setPanelList] = useRecoilState(multiPanelAtom);
    const isOpeningKnowledgeListPanel = panelList.some(p => p.type === Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST);
    const [viewSelectedKnowledge, setViewSelectedKnowledge] = useRecoilState(knowledgeViewSelectedAtom);
    const listenOnChangeViewSelectedKnowledge = useRecoilValue(listenOnChangeKnowledgeViewSelectedAtom);
    const totalTickets = useRecoilValue(publicKnowledgeHitNumberTicketAtom);
    const corporationDetail = useRecoilValue(corporationAtom);
    const [isUsingChatAI, setIsUsingChatAI] = useRecoilState(isUsingChatAIAtom);
    const viewKnowledgeProjectSelected = useRecoilValue(viewKnowledgeProjectSelectedAtom);
    const destinationPrjId = useRecoilValue(destinationPrjIdAtom);
    const { enqueueSnackbar } = useSnackbar();
    
    useEffect(() => {
        // re-check isUsingChatAI
        if (!isOpeningKnowledgeListPanel) {
            (
                async () => await Utils.isUsingFunctionService(
                    viewKnowledgeProjectSelected,
                    corporationDetail,
                    'usingChatAI',
                    setIsUsingChatAI
                )
            ) ()
        }

    }, [isOpeningKnowledgeListPanel]);

    const isReferenceKnowledgeDisabled = useMemo(() => {
        const isDisabled = !toggleUsingKnowledgeTicketList
            || isInvalidPrecondition
            || !isUsingKnowledgeService
            || !projectIdKnowledgeSelected;
        return isDisabled;
    }, [toggleUsingKnowledgeTicketList, isInvalidPrecondition, isUsingKnowledgeService, projectIdKnowledgeSelected]);

    const getTitleProject = () => {
        let titleProjectValue = projectIdKnowledgeSelected === Constant.KNOWLEDGE_VIEW_OPTION.ALL_KNOWLEDGE_IN_ORG
            ? trI18n('CM-LABEL-ALL-KNOWLEDGE-IN-ORG')
            : projectInfo?.projectName

        return (
            <div className="chat-ai-project-title">
                <OverflowTooltipContent
                    text={titleProjectValue}
                    tooltipMaxWidth={300}
                    placement="top"
                />
            </div>
        );
    };

    return (
        <div className="left">
            <div className="d-flex align-items-center justify-content-start">
                <Tooltip
                    title={trI18n('AI_CHAT_REFERENCE_KNOWLEDGE_LIST')}
                    placement="top"
                    arrow={true}
                    disableHoverListener={isReferenceKnowledgeDisabled}
                >
                    <button
                        className="btn-reset-default chat-ai-reference-knowledge-button"
                        disabled={isReferenceKnowledgeDisabled}
                        onClick={reOpenKnowledgePanel}
                    >
                        <ReferenceKnowledgeIcon isUsing={!isReferenceKnowledgeDisabled} />
                    </button>
                </Tooltip>
                {!isReferenceKnowledgeDisabled && (
                    getTitleProject()
                )}
                        
                <div className="group-btn-submit-reset">
                    {/* btn save search ViewSelectedKnowledge */}
                    <Tooltip
                        title={trI18n('LABEL-TOOLTIP-BUTTON-SAVE-SEARCH-CONDITION')}
                        arrow={true}
                        placement="top"
                        disableHoverListener={
                            !isOpeningKnowledgeListPanel
                            || Utils.isEmptyObject(viewSelectedKnowledge)
                            || !isUsingKnowledgeService
                        }
                    >
                        <div className={
                                `d-flex align-items-center justify-content-center gap-8 ${
                                    (!toggleUsingKnowledgeTicketList || !isUsingKnowledgeService) && 'hidden'
                                }`
                            }
                        >
                            <button
                                className='btn-reset-default chat-ai-reload-knowledge-button'
                                disabled={!isOpeningKnowledgeListPanel || Utils.isEmptyObject(viewSelectedKnowledge)}
                                onClick={() => handleUpdateSearchCondition({
                                    chatThreadId,
                                    setViewSelectedKnowledge,
                                    listenOnChangeViewSelectedKnowledge,
                                    panelList, 
                                    setPanelList, 
                                    panelId, 
                                    tabId,
                                    enqueueSnackbar,
                                    viewKnowledgeProjectSelected,
                                    totalTickets,
                                    isUsingChatAI,
                                    destinationPrjId
                                })}
                            >
                                <ReloadIcon width={16} height={16} />
                            </button>
                        </div>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
}
